import { BASE_CURRENCY, type Currency } from './enums.js';

/**
 * Money is always stored and transported as an integer number of cents in the
 * base currency (USD). Never use floats for money. The storefront currency
 * selector performs *display-only* conversion using the indicative rates below
 * — orders are always captured and settled in the base currency.
 */

/** Indicative USD -> currency multipliers. Display only, not for settlement. */
export const INDICATIVE_RATES: Record<Currency, number> = {
  USD: 1,
  PHP: 58.5,
  EUR: 0.92,
};

const CURRENCY_LOCALE: Record<Currency, string> = {
  USD: 'en-US',
  PHP: 'en-PH',
  EUR: 'en-IE',
};

/**
 * Convert an amount in base-currency cents to display-currency **minor units**
 * (also cents/centavos). Rounds to the nearest minor unit.
 */
export function convertFromBaseCents(baseCents: number, to: Currency): number {
  if (to === BASE_CURRENCY) return baseCents;
  return Math.round(baseCents * INDICATIVE_RATES[to]);
}

/**
 * Format base-currency cents for display in the requested currency.
 * e.g. formatMoney(1299, 'USD') -> "$12.99"
 */
export function formatMoney(baseCents: number, currency: Currency = BASE_CURRENCY): string {
  const minorUnits = convertFromBaseCents(baseCents, currency);
  return new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
    style: 'currency',
    currency,
  }).format(minorUnits / 100);
}

/** Format an amount already expressed in the given currency's cents. */
export function formatCents(cents: number, currency: Currency = BASE_CURRENCY): string {
  return new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/** Parse a decimal major-unit string (e.g. "12.99") into integer cents. */
export function parseToCents(value: string): number {
  const normalized = value.trim().replace(/[^0-9.]/g, '');
  const asNumber = Number.parseFloat(normalized);
  if (!Number.isFinite(asNumber)) {
    throw new Error(`Invalid money value: ${value}`);
  }
  return Math.round(asNumber * 100);
}

/** Sum line totals safely in integer cents. */
export function sumCents(amounts: readonly number[]): number {
  return amounts.reduce((total, n) => total + n, 0);
}
